import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
// import axios from '../../utils/axios';
import { get, post, put } from './api';
import { get as storeGet, put as storePut } from './apiStore';

type BenefitListItem = {
  id: string;
  name: string;
  image?: string;
  benefitType: string;
  tiers?: string[];
  toShowNames?: string[];
  price?: number | null;
  exhibitionEnv?: string;
  lpEventsData?: {
    businessUnitId: string;
    eventId: string;
  };
  redemptionId?: string;
  withdrawalStatus?: 'available' | 'insufficientPoints';
};

type BenefitState = {
  isLoading: boolean;
  isBenefitsLoading: boolean;
  hasMoreBenefits: boolean;
  benefitsRequestKey: string | null;
  isLoadingNotViva: boolean;
  error: any;
  benefitsList: BenefitListItem[];
  benefitTypes: string[];
  reservation: any;
  benefit: any;
  businessUnits: any;
  lpEvents: any;
  lpReservation: any;
};

const initialState: BenefitState = {
  isLoading: false,
  isBenefitsLoading: false,
  hasMoreBenefits: true,
  benefitsRequestKey: null,
  isLoadingNotViva: false,
  error: null,
  benefitsList: [],
  benefitTypes: [],
  benefit: null,
  reservation: null,
  businessUnits: null,
  lpEvents: null,
  lpReservation: null,
};

export const slice = createSlice({
  name: 'benefits',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },

    startLoadingNotViva(state) {
      state.isLoadingNotViva = true;
    },

    stopLoading(state) {
      state.isLoading = false;
    },

    stopLoadingNotViva(state) {
      state.isLoadingNotViva = false;
    },

    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    getBenefitsSuccess(state, action) {
      state.isLoading = false;
      state.benefitsList = Array.isArray(action.payload) ? action.payload : [];
    },

    startBenefitsPageLoading(state, action) {
      state.isBenefitsLoading = true;
      if (action.payload.page === 0) {
        state.benefitsList = [];
        state.benefitTypes = [];
        state.hasMoreBenefits = true;
        state.benefitsRequestKey = action.payload.requestKey;
      }
    },

    getBenefitsPageSuccess(state, action) {
      if (state.benefitsRequestKey !== action.payload.requestKey) return;

      const { benefits, benefitTypes, page, hasMore } = action.payload;
      const benefitKey = (benefit: BenefitListItem) =>
        benefit.redemptionId ?? `${benefit.withdrawalStatus ?? 'available'}:${benefit.id}`;
      const existingIds = new Set(state.benefitsList.map(benefitKey));
      const newBenefits = benefits.filter((benefit: BenefitState['benefitsList'][number]) => {
        const key = benefitKey(benefit);
        if (existingIds.has(key)) return false;
        existingIds.add(key);
        return true;
      });

      state.benefitsList = page === 0 ? benefits : [...state.benefitsList, ...newBenefits];
      state.benefitTypes = Array.from(new Set([...state.benefitTypes, ...benefitTypes]));
      state.hasMoreBenefits = hasMore && (page === 0 || newBenefits.length > 0);
      state.isBenefitsLoading = false;
      state.isLoading = false;
    },

    getBenefitsPageError(state, action) {
      if (state.benefitsRequestKey !== action.payload.requestKey) return;
      state.isBenefitsLoading = false;
      state.hasMoreBenefits = false;
      state.isLoading = false;
      state.error = action.payload.error;
    },

    getBenefitSuccess(state, action) {
      state.benefit = action.payload;
      state.isLoading = false;
    },

    getReservationSuccess(state, action) {
      state.reservation = action.payload;
      state.isLoading = false;
    },

    getBUSuccess(state, action) {
      state.businessUnits = action.payload;
      state.isLoading = false;
    },

    getLPEventsSuccess(state, action) {
      state.lpEvents = action.payload;
      state.isLoading = false;
    },

    clearLPEvents(state) {
      state.lpEvents = [];
    },

    getLPReservationSuccess(state, action) {
      state.lpReservation = action.payload;
      state.isLoading = false;
    },
  },
});

export default slice.reducer;

function normalizeWithdrawalEntries(
  entries: any[],
  withdrawalStatus: BenefitListItem['withdrawalStatus']
): BenefitListItem[] {
  return entries
    .map((entry): BenefitListItem | null => {
      const benefit = entry?.benefit ?? entry;
      const id = benefit?.benefitId ?? benefit?.id;
      if (!id) return null;

      return {
        id,
        name: benefit.name,
        image: benefit.image,
        benefitType: benefit.benefitType,
        tiers: benefit.tiers,
        toShowNames: benefit.toShowNames,
        price: benefit.price,
        exhibitionEnv: benefit.exhibitionEnv,
        lpEventsData: benefit.lpEventsData,
        redemptionId: entry.redemption?.redemptionId,
        withdrawalStatus,
      };
    })
    .filter((benefit): benefit is BenefitListItem => benefit !== null);
}

function readBenefitsPage(payload: any, page: number) {
  const hasWithdrawalGroups =
    Array.isArray(payload?.benefitsAvailableForWithdrawal) ||
    Array.isArray(payload?.benefitsWithoutEnoughPoints);
  const items: BenefitListItem[] = hasWithdrawalGroups
    ? [
        ...normalizeWithdrawalEntries(payload?.benefitsAvailableForWithdrawal ?? [], 'available'),
        ...normalizeWithdrawalEntries(payload?.benefitsWithoutEnoughPoints ?? [], 'insufficientPoints'),
      ]
    : Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.items)
          ? payload.items
          : [];
  const benefitTypes: string[] = Array.isArray(payload?.benefitTypes)
    ? payload.benefitTypes.filter((type: unknown): type is string => typeof type === 'string')
    : [];
  const pagination = payload?.pagination ?? payload?.meta ?? payload;
  let hasMore = items.length > 0;
  if (
    typeof pagination?.pageSize === 'number' &&
    pagination.pageSize > 0 &&
    typeof pagination?.totalAvailableForWithdrawal === 'number' &&
    typeof pagination?.totalWithoutEnoughPoints === 'number'
  ) {
    hasMore =
      (page + 1) * pagination.pageSize <
      pagination.totalAvailableForWithdrawal + pagination.totalWithoutEnoughPoints;
  } else if (typeof pagination?.hasNextPage === 'boolean') {
    hasMore = pagination.hasNextPage;
  } else if (typeof pagination?.hasMore === 'boolean') {
    hasMore = pagination.hasMore;
  } else if (typeof pagination?.totalPages === 'number') {
    hasMore = page + 1 < pagination.totalPages;
  }
  return { items, benefitTypes, hasMore };
}

type GetBenefitsOptions = {
  memberCode?: string;
  onlyRedeemedBenefits?: boolean;
  page?: number;
  requestKey?: string;
  benefitType?: string;
  search?: string;
};

export function getBenefits({
  memberCode,
  onlyRedeemedBenefits,
  page,
  requestKey = JSON.stringify([memberCode, onlyRedeemedBenefits]),
  benefitType,
  search,
}: GetBenefitsOptions = {}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    if (page !== undefined) {
      dispatch(slice.actions.startBenefitsPageLoading({ page, requestKey }));
    }
    try {
      const queryParams = new URLSearchParams();
      if (memberCode) queryParams.set('memberCode', memberCode);
      if (onlyRedeemedBenefits !== undefined) {
        queryParams.set('onlyRedeemedBenefits', String(onlyRedeemedBenefits));
      }
      if (page !== undefined) queryParams.set('page', String(page));
      if (benefitType) queryParams.set('benefitType', benefitType);
      if (search) queryParams.set('search', search);
      const query = queryParams.toString();

      let benefits;
      if (window.location.pathname.includes('/promoter')) {
        benefits = await get(`/benefits/all-benefits-for-promoter${query ? `?${query}` : ''}`);
      } else {
        benefits = await storeGet(`/benefits/all-benefits-for-promoter${query ? `?${query}` : ''}`);
      }
      if (page !== undefined) {
        const { items, benefitTypes, hasMore } = readBenefitsPage(benefits.data, page);
        dispatch(
          slice.actions.getBenefitsPageSuccess({
            benefits: items,
            benefitTypes,
            page,
            hasMore,
            requestKey,
          })
        );
      } else {
        dispatch(slice.actions.getBenefitsSuccess(readBenefitsPage(benefits.data, 0).items));
      }
    } catch (e) {
      console.log(e);
      if (page !== undefined) {
        dispatch(slice.actions.getBenefitsPageError({ error: e, requestKey }));
      } else {
        dispatch(slice.actions.getBenefitsSuccess([]));
        dispatch(slice.actions.hasError(e));
      }
    }
  };
}

export function getBusinessUnits() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await get('/benefits/lp-events/all-bu-lp-promoter');

      dispatch(slice.actions.getBUSuccess(response.data.businessUnit));
      dispatch(slice.actions.getLPEventsSuccess(response.data.event));
    } catch (e) {
      console.log(e);
      dispatch(slice.actions.hasError(e));
    }
  };
}

export function getLpEventsByBusinessUnit(businessUnitId: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await get(
        `/benefits/lp-events/lp-events-by-business-unit?id=${businessUnitId}`
      );

      const lpEventsByBusinessUnit = (response.data || []).map((item: any) => ({
        value: item._id,
        label: item.name,
      }));

      dispatch(slice.actions.getLPEventsSuccess(lpEventsByBusinessUnit));
    } catch (e) {
      console.log(e);
      dispatch(slice.actions.hasError(e));
    }
  };
}

export function getLPBenefits() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const benefits = await get('/benefits/product/valid-lp-events-products');
      const benefitsList = Array.isArray(benefits.data) ? benefits.data : benefits.data?.data;

      dispatch(slice.actions.getBenefitsSuccess(benefitsList));
    } catch (e) {
      console.log(e);
      dispatch(slice.actions.getBenefitsSuccess([]));
      dispatch(slice.actions.hasError(e));
    }
  };
}

export function getReservation(membercard: string, benefitId: string, benefitType: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      let res;
      if (window.location.pathname.includes('/promoter')) {
        res = await get(
          `/benefits/get-user-redemption?memberCard=${membercard}&benefitId=${benefitId}&benefitType=${benefitType}`
        );
      } else {
        res = await storeGet(
          `/benefits/get-user-redemption?memberCard=${membercard}&benefitId=${benefitId}&benefitType=${benefitType}`
        );
      }

      const reservation = res.data;

      if (reservation?.user?.phone) {
        if (reservation.user.phone.length === 11) {
          reservation.user.phone = reservation.user.phone.replace(
            /(\d{2})(\d{5})(\d{4})/,
            '($1) $2-$3'
          );
        } else {
          reservation.user.phone = reservation.user.phone.replace(
            /(\d{2})(\d{4})(\d{4})/,
            '($1) $2-$3'
          );
        }
      }
      dispatch(slice.actions.getReservationSuccess(reservation));
      return reservation;
    } catch (e) {
      console.log(e);
      dispatch(slice.actions.hasError(e));
      if (Array.isArray(e?.response?.data?.message)) {
        return { error: e?.response?.data?.message[0]?.msg };
      } else {
        return { error: e?.response?.data?.message || e?.message };
      }
    }
  };
}

export function getLPReservation(cpf: string, benefitId: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const res = await post(`/auth/validates-lp-events-user-redemption`, {
        cpf: cpf,
        productId: benefitId,
      });

      dispatch(slice.actions.getLPReservationSuccess(res.data));
      return res.data;
    } catch (e) {
      console.log(e);
      dispatch(slice.actions.hasError(e));
      if (Array.isArray(e?.response?.data?.message)) {
        return { error: e?.response?.data?.message[0]?.msg };
      } else {
        return { error: e?.response?.data?.message || e?.message };
      }
    }
  };
}

export function confirmReservation(memberCard: string, benefitId: string, benefitType: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      if (window.location.pathname.includes('/promoter')) {
        await put(`/benefits/confirm-use`, {
          memberCard,
          benefitId,
          benefitType,
        });
      } else {
        await storePut(`/benefits/confirm-use`, {
          memberCard,
          benefitId,
          benefitType,
        });
      }
      dispatch(slice.actions.stopLoading());
      return true;
    } catch (e) {
      console.log(e);
      dispatch(slice.actions.hasError(e));
      return e?.response?.data;
    }
  };
}

export function confirmLPReservation(cpf: string, productId: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      await post(`/benefits/confirm-use-lp-events-benefit`, {
        cpf,
        productId,
      });
      dispatch(slice.actions.stopLoading());
      return true;
    } catch (e) {
      console.log(e);
      dispatch(slice.actions.hasError(e));
      return e?.response?.data;
    }
  };
}

export function createReservation(
  memberCard: string,
  benefitId: string,
  benefitType: string,
  bypassPersonLimit: boolean = false,
  bypassRedeemLimit: boolean = false,
  description: string = '',
  children: number = 0,
  adults: number = 0,
  spendPoints: boolean = true,
  confirmOverbooking: boolean = false
) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      if (window.location.pathname.includes('/promoter')) {
        const response = await put(`/benefits/redeem-and-confirm-use`, {
          memberCard,
          benefitId,
          benefitType,
          bypassPersonLimit,
          bypassRedeemLimit,
          children,
          adults,
          description,
          spendPoints,
          confirmOverbooking,
        });
        dispatch(slice.actions.stopLoading());
        return response.data;
      } else {
        const response = await storePut(`/benefits/redeem-and-confirm-use`, {
          memberCard,
          benefitId,
          benefitType,
          bypassPersonLimit,
          bypassRedeemLimit,
          children,
          adults,
          description,
          spendPoints,
          confirmOverbooking,
        });
        dispatch(slice.actions.stopLoading());
        return response.data;
      }
    } catch (e) {
      dispatch(slice.actions.hasError(e));
      return e?.response?.data;
    }
  };
}

export function getBenefitDetails(benefitId: string, benefitType: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      let benefit;
      if (window.location.pathname.includes('/promoter')) {
        benefit = await get(
          `/benefits/promoter-details?benefitId=${benefitId}&benefitType=${benefitType}`
        );
      } else {
        benefit = await storeGet(
          `/benefits/promoter-details?benefitId=${benefitId}&benefitType=${benefitType}`
        );
      }
      dispatch(slice.actions.getBenefitSuccess(benefit.data[0]));
    } catch (e) {
      console.log(e);
      dispatch(slice.actions.hasError(e));
    }
  };
}

export function getEventDetails(benefitId: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const benefit = await get(`/benefits/events/available-times?eventId=${benefitId}`);
      dispatch(slice.actions.getBenefitSuccess(benefit.data));
    } catch (e) {
      console.log(e);
      dispatch(slice.actions.hasError(e));
    }
  };
}

export function createBenefitNotViva(
  user: any,
  benefit: any,
  bypassPersonLimit: boolean = false,
  bypassRedeemLimit: boolean = false,
  description: string = ''
) {
  return async () => {
    dispatch(slice.actions.startLoadingNotViva());
    try {
      await post(`/benefits/redeem-and-confirm-use-not-viva`, {
        user,
        benefit,
        bypassPersonLimit,
        bypassRedeemLimit,
        description,
      });
      dispatch(slice.actions.stopLoadingNotViva());
      return true;
    } catch (e) {
      dispatch(slice.actions.stopLoadingNotViva());
      console.log(e);
      dispatch(slice.actions.hasError(e));
      throw e?.response?.data;
    }
  };
}
